import React, { useState, useCallback, useEffect } from 'react';
import { Template, TemplateBlock, ABTestVariant, VariantChange, TestableElement, ElementSuggestion } from '../../../types/template';
import styles from './VariantEditor.module.css';
import { clickable } from "components/ui/interactive";

interface VariantEditorProps {
  controlTemplate: Template;
  testVariant: ABTestVariant;
  testableElements: TestableElement[];
  onVariantChange: (variantId: string, changes: VariantChange[]) => void;
  onPreviewDevice: (device: 'desktop' | 'tablet' | 'mobile') => void;
  isVisible: boolean;
  onClose: () => void;
}

interface ChangeTracker {
  blockId: string;
  changeType: VariantChange['changeType'];
  description: string;
  oldValue: any;
  newValue: any;
  timestamp: Date;
  confidence?: number;
}

interface AIVariantSuggestion {
  id: string;
  title: string;
  description: string;
  targetBlockId: string;
  changeType: VariantChange['changeType'];
  suggestedValue: any;
  confidence: number;
  expectedImpact: number;
  reasoning: string;
}

interface PerformancePrediction {
  expectedImprovement: number;
  confidenceInterval: [number, number];
  statisticalPower: number;
  recommendedSampleSize: number;
  estimatedDuration: number;
}

const VariantEditor: React.FC<VariantEditorProps> = ({
  controlTemplate,
  testVariant,
  testableElements,
  onVariantChange,
  onPreviewDevice,
  isVisible,
  onClose,
}) => {
  // State management
  const [activeDevice, setActiveDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [syncChanges, setSyncChanges] = useState(true);
  const [changeHistory, setChangeHistory] = useState<ChangeTracker[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showDifferences, setShowDifferences] = useState(true);
  const [aiSuggestions, setAiSuggestions] = useState<AIVariantSuggestion[]>([]);
  const [performancePrediction, setPerformancePrediction] = useState<PerformancePrediction | null>(null);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);

  // Local template state to handle immediate updates
  const [localVariantTemplate, setLocalVariantTemplate] = useState<Template>(testVariant.template);
  const [templateVersion, setTemplateVersion] = useState(0);

  // Create a deep copy of the control template to prevent mutations
  const [originalControlTemplate] = useState<Template>(() => JSON.parse(JSON.stringify(controlTemplate)));

  // Update local template when testVariant changes
  useEffect(() => {
    setLocalVariantTemplate(testVariant.template);
    setTemplateVersion(prev => prev + 1);
  }, [testVariant.template]);

  // Generate AI suggestions when component mounts or testable elements change
  useEffect(() => {
    generateAISuggestions();
    calculatePerformancePrediction();
  }, [testableElements, testVariant]);

  // Generate AI-powered variant suggestions
  const generateAISuggestions = useCallback(() => {
    const suggestions: AIVariantSuggestion[] = [];

    testableElements.forEach(element => {
      element.suggestions.forEach(suggestion => {
        // Convert element suggestions to variant suggestions
        const variantSuggestion: AIVariantSuggestion = {
          id: `variant-${element.blockId}-${suggestion.id}`,
          title: `${element.elementName}: ${suggestion.title}`,
          description: suggestion.description,
          targetBlockId: element.blockId,
          changeType: getChangeTypeFromSuggestion(suggestion, element.testType),
          suggestedValue: suggestion.suggestedChange,
          confidence: suggestion.confidence,
          expectedImpact: suggestion.impactMagnitude,
          reasoning: generateReasoningText(suggestion, element),
        };
        suggestions.push(variantSuggestion);
      });

      // Add additional AI-generated suggestions based on element type
      const additionalSuggestions = generateAdditionalSuggestions(element);
      suggestions.push(...additionalSuggestions);
    });

    // Sort by confidence and expected impact
    suggestions.sort((a, b) => (b.confidence + b.expectedImpact) - (a.confidence + a.expectedImpact));

    setAiSuggestions(suggestions.slice(0, 10)); // Limit to top 10 suggestions
  }, [testableElements]);

  // Calculate performance prediction based on changes
  const calculatePerformancePrediction = useCallback(() => {
    const totalExpectedImpact = changeHistory.reduce((sum, change) => {
      return sum + (change.confidence || 50) * 0.01; // Convert confidence to impact factor
    }, 0);

    const baseImprovement = Math.min(totalExpectedImpact * 5, 50); // Cap at 50% improvement
    const confidenceRange = baseImprovement * 0.3; // ±30% confidence interval

    const prediction: PerformancePrediction = {
      expectedImprovement: Math.round(baseImprovement * 100) / 100,
      confidenceInterval: [
        Math.max(0, Math.round((baseImprovement - confidenceRange) * 100) / 100),
        Math.round((baseImprovement + confidenceRange) * 100) / 100
      ],
      statisticalPower: Math.min(80 + (changeHistory.length * 5), 95),
      recommendedSampleSize: Math.max(1000, Math.round(2000 / Math.max(baseImprovement, 0.05))),
      estimatedDuration: Math.max(7, Math.round(14 / Math.max(baseImprovement * 0.1, 0.01))),
    };

    setPerformancePrediction(prediction);
  }, [changeHistory]);

  // Helper functions
  const getChangeTypeFromSuggestion = (suggestion: ElementSuggestion, testType: TestableElement['testType']): VariantChange['changeType'] => {
    // Check if the suggestion involves style changes (like colors, sizes, etc.)
    if (suggestion.suggestedChange?.backgroundColor ||
      suggestion.suggestedChange?.color ||
      suggestion.suggestedChange?.fontSize ||
      suggestion.suggestedChange?.padding ||
      suggestion.title.toLowerCase().includes('color') ||
      suggestion.title.toLowerCase().includes('size')) {
      return 'style';
    }

    switch (testType) {
      case 'content':
      case 'headline':
        return 'content';
      case 'cta':
        // CTA changes can be either content (text) or style (appearance)
        return suggestion.title.toLowerCase().includes('text') ? 'content' : 'style';
      case 'style':
        return 'style';
      case 'image':
        return 'content';
      case 'position':
        return 'position';
      default:
        return 'content';
    }
  };

  const generateReasoningText = (suggestion: ElementSuggestion, element: TestableElement): string => {
    const reasons = [
      `Based on ${suggestion.type === 'ai_generated' ? 'AI analysis' : 'best practices'}`,
      `${suggestion.confidence}% confidence in positive impact`,
      `Expected ${suggestion.impactMagnitude}% improvement`,
      `Optimized for ${element.testType} testing`,
    ];
    return reasons.join('. ');
  };

  const generateAdditionalSuggestions = (element: TestableElement): AIVariantSuggestion[] => {
    const suggestions: AIVariantSuggestion[] = [];
    const block = controlTemplate.content.find(b => b.id === element.blockId);

    if (!block) return suggestions;

    // Generate color variations for buttons
    if (element.blockType === 'button') {
      const colorSuggestions = [
        { color: '#FF6B35', name: 'Orange', impact: 15 },
        { color: '#2ECC71', name: 'Green', impact: 12 },
        { color: '#E74C3C', name: 'Red', impact: 18 },
        { color: '#3498DB', name: 'Blue', impact: 10 },
      ];

      colorSuggestions.forEach(colorSug => {
        suggestions.push({
          id: `color-${element.blockId}-${colorSug.name.toLowerCase()}`,
          title: `${element.elementName}: ${colorSug.name} Color`,
          description: `Test ${colorSug.name.toLowerCase()} button color for better visibility and conversion`,
          targetBlockId: element.blockId,
          changeType: 'style',
          suggestedValue: { backgroundColor: colorSug.color },
          confidence: 75,
          expectedImpact: colorSug.impact,
          reasoning: `${colorSug.name} buttons typically show ${colorSug.impact}% better performance in A/B tests`,
        });
      });
    }

    // Generate size variations
    if (element.blockType === 'button' || element.blockType === 'text') {
      const sizeSuggestions = [
        { size: 'larger', multiplier: 1.2, impact: 8 },
        { size: 'smaller', multiplier: 0.9, impact: 5 },
      ];

      sizeSuggestions.forEach(sizeSug => {
        suggestions.push({
          id: `size-${element.blockId}-${sizeSug.size}`,
          title: `${element.elementName}: ${sizeSug.size} Size`,
          description: `Test ${sizeSug.size} ${element.blockType} for better visual hierarchy`,
          targetBlockId: element.blockId,
          changeType: 'style',
          suggestedValue: {
            fontSize: element.blockType === 'text' ? `${sizeSug.multiplier}em` : undefined,
            padding: element.blockType === 'button' ? `${sizeSug.multiplier}em` : undefined,
          },
          confidence: 65,
          expectedImpact: sizeSug.impact,
          reasoning: `${sizeSug.size} elements can improve visual hierarchy and user attention`,
        });
      });
    }

    return suggestions;
  };

  // Handle applying AI suggestion
  const handleApplyAISuggestion = useCallback((suggestion: AIVariantSuggestion) => {
    const change: ChangeTracker = {
      blockId: suggestion.targetBlockId,
      changeType: suggestion.changeType,
      description: suggestion.title,
      oldValue: getCurrentBlockValue(suggestion.targetBlockId, suggestion.changeType),
      newValue: suggestion.suggestedValue,
      timestamp: new Date(),
      confidence: suggestion.confidence,
    };

    setChangeHistory(prev => [...prev, change]);

    // Apply the change to the variant
    const variantChange: VariantChange = {
      blockId: suggestion.targetBlockId,
      changeType: suggestion.changeType,
      description: suggestion.title,
      oldValue: change.oldValue,
      newValue: change.newValue,
      confidence: suggestion.confidence,
    };

    onVariantChange(testVariant.id, [variantChange]);
  }, [testVariant.id, onVariantChange]);

  // Get current block value for change tracking
  const getCurrentBlockValue = useCallback((blockId: string, changeType: VariantChange['changeType']) => {
    const block = testVariant.template.content.find(b => b.id === blockId);
    if (!block) return null;

    switch (changeType) {
      case 'content':
        return block.content;
      case 'style':
        return block.styles;
      case 'position':
        return block.position;
      case 'visibility':
        return block.metadata.hidden;
      default:
        return null;
    }
  }, [testVariant.template.content]);

  // Handle device preview change
  const handleDeviceChange = useCallback((device: 'desktop' | 'tablet' | 'mobile') => {
    setActiveDevice(device);
    onPreviewDevice(device);
  }, [onPreviewDevice]);

  // Handle block selection
  const handleBlockSelect = useCallback((blockId: string) => {
    if (bulkEditMode) {
      setSelectedBlocks(prev =>
        prev.includes(blockId)
          ? prev.filter(id => id !== blockId)
          : [...prev, blockId]
      );
    } else {
      setSelectedBlockId(blockId);
    }
  }, [bulkEditMode]);

  // Handle bulk operations
  const handleBulkStyleChange = useCallback((styleUpdates: any) => {
    const changes: VariantChange[] = selectedBlocks.map(blockId => ({
      blockId,
      changeType: 'style',
      description: `Bulk style update: ${Object.keys(styleUpdates).join(', ')}`,
      oldValue: getCurrentBlockValue(blockId, 'style'),
      newValue: styleUpdates,
    }));

    onVariantChange(testVariant.id, changes);

    // Update change history
    const trackerChanges: ChangeTracker[] = changes.map(change => ({
      blockId: change.blockId,
      changeType: change.changeType,
      description: change.description,
      oldValue: change.oldValue,
      newValue: change.newValue,
      timestamp: new Date(),
      confidence: 70,
    }));

    setChangeHistory(prev => [...prev, ...trackerChanges]);
  }, [selectedBlocks, testVariant.id, onVariantChange, getCurrentBlockValue]);

  // Render template preview
  const renderTemplatePreview = (template: Template, isVariant: boolean = false) => {
    return (
      <div className={`${styles.templatePreview} ${styles[activeDevice]} ${isVariant ? styles.variant : styles.control}`}>
        <div className={styles.templateContent} {...clickable(isVariant ? handleOutsideClick : undefined)}>
          {template.content.map(block => (
            <div
              key={`${block.id}-${isVariant ? templateVersion : 'control'}`}
              className={`${styles.templateBlock} ${styles[block.type]} ${selectedBlockId === block.id ? styles.selected : ''
                } ${selectedBlocks.includes(block.id) ? styles.bulkSelected : ''
                } ${showDifferences && hasBlockChanged(block.id) ? styles.changed : ''
                }`}
              {...clickable(() => isVariant ? handleBlockSelect(block.id) : undefined)}
              style={{ cursor: isVariant ? 'pointer' : 'default' }}
            >
              {renderBlockContent(block, isVariant)}
              {showDifferences && hasBlockChanged(block.id) && (
                <div className={styles.changeIndicator}>
                  <span className={styles.changeBadge}>Modified</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Check if block has been changed
  const hasBlockChanged = useCallback((blockId: string): boolean => {
    return changeHistory.some(change => change.blockId === blockId);
  }, [changeHistory]);

  // Handle text content changes
  const handleTextChange = useCallback((blockId: string, newText: string) => {
    const block = localVariantTemplate.content.find(b => b.id === blockId);
    const oldValue = { text: block?.content.text };
    const newValue = { text: newText };

    // Update local template immediately for instant UI feedback
    setLocalVariantTemplate(prevTemplate => {
      const newTemplate = {
        ...prevTemplate,
        content: prevTemplate.content.map(b =>
          b.id === blockId
            ? { ...b, content: { ...b.content, text: newText, html: newText } }
            : b
        )
      };
      console.log('Updated local template:', newTemplate);
      return newTemplate;
    });

    // Force re-render by updating version
    setTemplateVersion(prev => prev + 1);

    const change: VariantChange = {
      blockId,
      changeType: 'content',
      description: 'Updated text content',
      oldValue,
      newValue,
    };

    // Track the change
    setChangeHistory(prev => [...prev, {
      blockId,
      changeType: 'content',
      description: `Updated text content`,
      oldValue,
      newValue,
      timestamp: new Date(),
    }]);

    // Apply the change to parent component
    onVariantChange(testVariant.id, [change]);
  }, [localVariantTemplate, testVariant.id, onVariantChange]);

  // Handle clicking outside to deselect
  const handleOutsideClick = useCallback((e: React.MouseEvent) => {
    // Only deselect if not in bulk edit mode and clicking on the template content area
    if (!bulkEditMode && e.target === e.currentTarget) {
      setSelectedBlockId(null);
    }
  }, [bulkEditMode]);

  // Get current text content for a block (from local template if variant, original if control)
  const getCurrentTextContent = (blockId: string, isVariant: boolean) => {
    if (isVariant) {
      const localBlock = localVariantTemplate.content.find(b => b.id === blockId);
      return localBlock?.content.text || localBlock?.content.html || '';
    } else {
      // Always use the original control template for control blocks
      const controlBlock = originalControlTemplate.content.find(b => b.id === blockId);
      return controlBlock?.content.text || controlBlock?.content.html || '';
    }
  };

  // Render block content based on type
  const renderBlockContent = (block: TemplateBlock, isVariant: boolean = false) => {
    const isSelected = selectedBlockId === block.id && isVariant;
    const currentTextContent = getCurrentTextContent(block.id, isVariant);

    switch (block.type) {
      case 'text':
        if (isSelected && isVariant) {
          return (
            <textarea
              className={`${styles.textBlock} ${styles.editableText}`}
              style={{
                ...block.styles.typography,
                border: '2px solid #3B82F6',
                borderRadius: '4px',
                padding: '8px',
                resize: 'vertical',
                minHeight: '60px',
                width: '100%',
                fontFamily: 'inherit',
              }}
              value={currentTextContent}
              onChange={(e) => handleTextChange(block.id, e.target.value)}
              placeholder="Enter text content..."
              autoFocus
            />
          );
        } else {
          return (
            <div
              className={styles.textBlock}
              style={block.styles.typography}
              dangerouslySetInnerHTML={{ __html: currentTextContent || 'Text content' }}
            />
          );
        }
      case 'button':
        return (
          <button
            className={styles.buttonBlock}
            style={{
              ...block.styles.typography,
              ...block.styles.background,
              // Convert layout properties to valid CSS
              width: block.styles.layout?.width,
              height: block.styles.layout?.height,
              position: block.styles.layout?.position as React.CSSProperties['position'],
              zIndex: block.styles.layout?.zIndex,
              // Convert SpacingValues to CSS strings
              marginTop: block.styles.layout?.margin?.top,
              marginRight: block.styles.layout?.margin?.right,
              marginBottom: block.styles.layout?.margin?.bottom,
              marginLeft: block.styles.layout?.margin?.left,
              paddingTop: block.styles.layout?.padding?.top,
              paddingRight: block.styles.layout?.padding?.right,
              paddingBottom: block.styles.layout?.padding?.bottom,
              paddingLeft: block.styles.layout?.padding?.left,
            }}
          >
            {block.content.text || 'Button'}
          </button>
        );
      case 'image':
        return (
          <img
            className={styles.imageBlock}
            src={block.content.src || 'https://picsum.photos/320/180?random=5'}
            alt={block.content.alt || 'Image'}
            style={{
              // Convert layout properties to valid CSS
              width: block.styles.layout?.width,
              height: block.styles.layout?.height,
              position: block.styles.layout?.position as React.CSSProperties['position'],
              zIndex: block.styles.layout?.zIndex,
              display: block.styles.layout?.display,
              // Convert SpacingValues to CSS strings
              marginTop: block.styles.layout?.margin?.top,
              marginRight: block.styles.layout?.margin?.right,
              marginBottom: block.styles.layout?.margin?.bottom,
              marginLeft: block.styles.layout?.margin?.left,
              paddingTop: block.styles.layout?.padding?.top,
              paddingRight: block.styles.layout?.padding?.right,
              paddingBottom: block.styles.layout?.padding?.bottom,
              paddingLeft: block.styles.layout?.padding?.left,
            }}
          />
        );
      default:
        return <div className={styles.genericBlock}>[{block.type} block]</div>;
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-content">Variant Editor</h2>
              <p className="text-sm text-content-secondary mt-1">
                Editing: {testVariant.name} • Template: {controlTemplate.name}
              </p>
            </div>

            {/* Header Controls */}
            <div className="flex items-center space-x-4">
              {/* Device Preview Selector */}
              <div className="flex items-center space-x-1 bg-gray-100 dark:bg-dark-700 p-1 rounded-lg">
                {(['desktop', 'tablet', 'mobile'] as const).map(device => (
                  <button
                    key={device}
                    onClick={() => handleDeviceChange(device)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${activeDevice === device
                      ? ' text-blue-600 dark:text-blue-300 shadow-sm'
                      : 'text-content-secondary hover:text-content'
                      }`}
                  >
                    {device.charAt(0).toUpperCase() + device.slice(1)}
                  </button>
                ))}
              </div>

              {/* Sync Changes Toggle */}
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={syncChanges}
                  onChange={(e) => setSyncChanges(e.target.checked)}
                  className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Sync Changes</span>
              </label>

              {/* Show Differences Toggle */}
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showDifferences}
                  onChange={(e) => setShowDifferences(e.target.checked)}
                  className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Show Differences</span>
              </label>

              <button
                onClick={onClose}
                className="text-content-subtle hover:text-content-secondary transition-colors p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Side-by-Side Comparison */}
          <div className="flex-1 flex">
            {/* Control Variant (Left) */}
            <div className="w-1/2 border-r border-line overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-content">Control (Original)</h3>
                  <span className="text-sm text-content-muted">
                    {controlTemplate.content.length} blocks
                  </span>
                </div>
                {renderTemplatePreview(originalControlTemplate, false)}
              </div>
            </div>

            {/* Test Variant (Right) */}
            <div className="w-1/2 overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-content">
                    Variant: {testVariant.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-content-muted">
                      {changeHistory.length} changes
                    </span>
                    {performancePrediction && (
                      <span className="text-sm text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full">
                        +{performancePrediction.expectedImprovement}% predicted
                      </span>
                    )}
                  </div>
                </div>
                <div key={templateVersion}>
                  {renderTemplatePreview(localVariantTemplate, true)}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel: AI Suggestions & Tools */}
          <div className="w-80 border-l border-line overflow-y-auto">
            <div className="p-4 space-y-6">
              {/* Bulk Edit Mode Toggle */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-content">Editing Tools</h3>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={bulkEditMode}
                    onChange={(e) => setBulkEditMode(e.target.checked)}
                    className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Bulk Edit</span>
                </label>
              </div>

              {/* Bulk Operations */}
              {bulkEditMode && (
                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                  {selectedBlocks.length > 0 ? (
                    <>
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                        Bulk Operations ({selectedBlocks.length} selected)
                      </h4>
                      <div className="space-y-2">
                        <button
                          onClick={() => handleBulkStyleChange({
                            typography: { fontSize: '1.1em' }
                          })}
                          className="w-full text-left px-3 py-2 text-sm bg-surface rounded border hover:bg-gray-50 dark:hover:bg-dark-700"
                        >
                          Increase Font Size
                        </button>
                        <button
                          onClick={() => handleBulkStyleChange({
                            typography: { fontWeight: 'bold' }
                          })}
                          className="w-full text-left px-3 py-2 text-sm bg-surface rounded border hover:bg-gray-50 dark:hover:bg-dark-700"
                        >
                          Make Bold
                        </button>
                        <button
                          onClick={() => handleBulkStyleChange({
                            layout: { margin: { top: '1em', bottom: '1em', left: '0', right: '0' } }
                          })}
                          className="w-full text-left px-3 py-2 text-sm bg-surface rounded border hover:bg-gray-50 dark:hover:bg-dark-700"
                        >
                          Add Spacing
                        </button>
                        <button
                          onClick={() => setSelectedBlocks([])}
                          className="w-full text-left px-3 py-2 text-sm bg-gray-100 dark:bg-dark-700 rounded border hover:bg-gray-200 dark:hover:bg-dark-600 text-content-secondary"
                        >
                          Clear Selection
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                        Bulk Edit Mode
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-300 mb-3">
                        Click on multiple blocks in the variant to select them for bulk operations.
                      </p>
                      <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                        <div>• Selected blocks will have a green border</div>
                        <div>• Click blocks to add/remove from selection</div>
                        <div>• Apply changes to all selected blocks at once</div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Performance Prediction */}
              {performancePrediction && (
                <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-green-900 dark:text-green-300 mb-2">Performance Prediction</h4>
                  <div className="space-y-2 text-sm text-green-800 dark:text-green-300">
                    <div className="flex justify-between">
                      <span>Expected Improvement:</span>
                      <span className="font-medium">+{performancePrediction.expectedImprovement}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Confidence Interval:</span>
                      <span className="font-medium">
                        {performancePrediction.confidenceInterval[0]}% - {performancePrediction.confidenceInterval[1]}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Statistical Power:</span>
                      <span className="font-medium">{performancePrediction.statisticalPower}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Recommended Sample:</span>
                      <span className="font-medium">{performancePrediction.recommendedSampleSize.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Estimated Duration:</span>
                      <span className="font-medium">{performancePrediction.estimatedDuration} days</span>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Suggestions */}
              <div>
                <h4 className="text-sm font-medium text-content mb-3">AI Suggestions</h4>
                <div className="space-y-3">
                  {aiSuggestions.slice(0, 5).map(suggestion => (
                    <div key={suggestion.id} className="border border-line rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {suggestion.title}
                        </h5>
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                            {suggestion.confidence}%
                          </span>
                          <span className="text-xs text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                            +{suggestion.expectedImpact}%
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-content-secondary mb-2">
                        {suggestion.description}
                      </p>
                      <p className="text-xs text-content-muted mb-3">
                        {suggestion.reasoning}
                      </p>
                      <button
                        onClick={() => handleApplyAISuggestion(suggestion)}
                        className="w-full px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        Apply Suggestion
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Change History */}
              {changeHistory.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-content mb-3">Change History</h4>
                  <div className="space-y-2">
                    {changeHistory.slice(-5).map((change, index) => (
                      <div key={index} className="text-xs bg-gray-50 dark:bg-dark-700 p-2 rounded">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{change.description}</div>
                        <div className="text-content-secondary mt-1">
                          {change.timestamp.toLocaleTimeString()}
                          {change.confidence && (
                            <span className="ml-2 text-blue-600 dark:text-blue-300">
                              {change.confidence}% confidence
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-line flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-content-secondary">
              {changeHistory.length} changes made
            </span>
            {performancePrediction && (
              <span className="text-sm text-green-600 dark:text-green-300">
                Expected improvement: +{performancePrediction.expectedImprovement}%
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setChangeHistory([])}
              className="px-4 py-2 text-sm text-content-secondary hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              disabled={changeHistory.length === 0}
            >
              Reset Changes
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => {
                // Save variant changes
                console.log('Saving variant with changes:', changeHistory);
                onClose();
              }}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Variant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantEditor;